import React, {
  createContext,
  useState,
  useContext,
  useCallback,
  useEffect,
  useRef,
  useMemo,
} from 'react';
import Toast from '../components/Toast';

const ToastContext = createContext();

const VALID_TYPES = ['success', 'error', 'warning', 'info'];
const AUTO_DISMISS_MS = 3000;

let toastCounter = 0;

/**
 * ToastProvider — Toast 알림을 전역으로 관리하는 Provider
 *
 * Provider 내부에 Toast 렌더링 영역(toast-container)을 포함하여,
 * 별도의 Toast 컨테이너를 Routes 외부에 배치할 필요 없음.
 *
 * 제공하는 값:
 * - showToast({ type, message }): Toast 알림 표시 함수
 *
 * 사용:
 *   const { showToast } = useToast();
 *   showToast({ type: 'success', message: '저장되었습니다' });
 */
export const ToastProvider = ({ children }) => {
  const [toasts, setToasts] = useState([]);
  const timersRef = useRef({});

  /**
   * Toast 제거
   * @param {string} id - 제거할 Toast의 고유 식별자
   */
  const removeToast = useCallback((id) => {
    // 타이머 정리
    if (timersRef.current[id]) {
      clearTimeout(timersRef.current[id]);
      delete timersRef.current[id];
    }
    setToasts((prev) => prev.filter((toast) => toast.id !== id));
  }, []);

  /**
   * Toast 표시
   * 빈 메시지는 무시하고, 유효하지 않은 type은 'info'로 폴백한다.
   * @param {{ type: string, message: string }} params
   */
  const showToast = useCallback(
    ({ type, message }) => {
      // 빈 메시지 무시
      if (!message || (typeof message === 'string' && message.trim() === '')) {
        return;
      }

      // 유효하지 않은 type은 'info'로 폴백
      const validType = VALID_TYPES.includes(type) ? type : 'info';

      const id = `toast-${Date.now()}-${toastCounter++}`;
      const newToast = {
        id,
        type: validType,
        message,
        createdAt: Date.now(),
      };

      setToasts((prev) => [...prev, newToast]);

      // 3초 자동 소멸 타이머
      timersRef.current[id] = setTimeout(() => {
        removeToast(id);
      }, AUTO_DISMISS_MS);
    },
    [removeToast]
  );

  // 컴포넌트 언마운트 시 모든 타이머 정리
  useEffect(() => {
    const timers = timersRef.current;
    return () => {
      Object.values(timers).forEach((timer) => clearTimeout(timer));
    };
  }, []);

  // Context value를 useMemo로 메모이제이션하여 불필요한 리렌더 방지
  const value = useMemo(() => ({ showToast }), [showToast]);

  return (
    <ToastContext.Provider value={value}>
      {children}
      {/* Toast 렌더링 영역 — Provider 내부에 포함 */}
      <div className="toast-container" aria-live="polite">
        {toasts.map((toast) => (
          <Toast
            key={toast.id}
            id={toast.id}
            type={toast.type}
            message={toast.message}
            onClose={() => removeToast(toast.id)}
          />
        ))}
      </div>
    </ToastContext.Provider>
  );
};

/**
 * useToast 커스텀 훅
 * ToastProvider 외부에서 호출 시 에러를 throw한다.
 * @returns {{ showToast: Function }}
 */
export const useToast = () => {
  const context = useContext(ToastContext);
  if (!context) {
    throw new Error('useToast must be used within ToastProvider');
  }
  return context;
};
